import { LOTTERY_ARCHIVED } from 'config/constants/lottery'
import { MIGRATION_ARCHIVED } from 'config/constants/migration'
import {
  MenuItemsType,
  SyncAltIcon,
  EarnFillIcon,
  EarnIcon,
  ChartIcon,
  AccountIcon,
  MoreHorizontalIcon,
  DropdownMenuItems,
} from '@pancakeswap/uikit'
import { ContextApi } from '@pancakeswap/localization'
import { SUPPORTED_CHAIN_IDS as POOL_SUPPORTED_CHAINS } from '@pancakeswap/pools'

import { SUPPORT_FARMS, SUPPORT_ONLY_BSC } from 'config/constants/supportChains'
import { CAD1_LIVE, CAD1_VISIBLE, cad1Paths } from 'views/Cad1/config'
import { CupFillIcon, CupIcon } from '../icons/Cup'
import { pwaMoreItems } from '../../../pwa/menu'
import ProfileFillIcon from './ProfileFillIcon'

export type ConfigMenuDropDownItemsType = DropdownMenuItems & { hideSubNav?: boolean; matchPrefix?: string }
export type ConfigMenuItemsType = Omit<MenuItemsType, 'items'> & { hideSubNav?: boolean; image?: string } & {
  items?: ConfigMenuDropDownItemsType[]
  /** stable name of a top-level tab (the bottom bar picks and orders tabs by it) */
  id?: 'markets' | 'pools' | 'swap' | 'competitions' | 'profile' | 'more'
  /** the label in the phone bottom bar, when shorter than the top bar's */
  mobileLabel?: string
}

const addMenuItemSupported = (item, chainId) => {
  if (!chainId || !item.supportChainIds) {
    return item
  }
  if (item.supportChainIds?.includes(chainId)) {
    return item
  }
  return {
    ...item,
    disabled: true,
  }
}

/**
 * Navigation (Bez, 2026-10-02, second pass; Competitions promoted 2026-10-06).
 * Top bar:    Markets · Pools · [Swap] · Competitions · Profile · ⋯
 * Bottom bar: Markets · Compete · [Swap] · Pools · More   (five slots; see bottomNavItems below)
 * Swap is the featured centre (a raised button on the phone, an accented pill on desktop) and goes straight to the swap page with no
 * submenu. Markets is the Info hub (/info) plus the token list. Competitions goes straight to the list (/trading-competition) and shows the
 * live competitions' token logos in a green ring (2026-10-07; a dot before), the logos opening the competition (hooks/useCompetitionLive).
 * Every other destination is in More. No route changed.
 */
const config: (
  t: ContextApi['t'],
  isDark: boolean,
  languageCode?: string,
  chainId?: number,
) => ConfigMenuItemsType[] = (t, isDark, languageCode, chainId) => {
  // Trading Competition left this list on 2026-10-06: it is a top-level tab now (one entry per destination, no duplicate)
  const moreItems: Array<ConfigMenuDropDownItemsType & { supportChainIds?: number[] }> = [
    // Click to Win left on 2026-10-08 (Bez: no use, clutter; archive/click-to-win-20261008)
    { label: t('Prediction Markets'), href: '/prediction-markets', status: { text: t('Demo'), color: 'warning' } },
    // Buy Crypto (fiat on-ramp) left on 2026-10-07: Transak declined, fees not worth it (archive/onramp-20261007)
    { label: t('Lock Tools'), href: '/cadinu-lock', supportChainIds: SUPPORT_ONLY_BSC },
    // CAD1 token launcher (2026-10-09): only on the Stage build or a build with a launcher configured; production stays as it is until Bez
    // opens the soft launch. "In testing", no date, until a live launcher (v2) is configured (Bez 2026-10-10: the test is finished): then no
    // chip. The top and bottom bars are unchanged.
    ...(CAD1_VISIBLE
      ? [{ label: t('Launch a token'), href: cad1Paths.home, ...(CAD1_LIVE ? {} : { status: { text: t('In testing'), color: 'warning' as const } }) }]
      : []),
    { label: t('Cadinu NFTs'), href: '/u?buy=1#nfts' },
    { label: t('Governance'), href: '/governance', supportChainIds: SUPPORT_ONLY_BSC },
    // parked products keep their pages and claim paths; when archived they are listed on /legacy only
    ...(LOTTERY_ARCHIVED ? [] : [{ label: t('Lottery'), href: '/lottery', supportChainIds: SUPPORT_ONLY_BSC }]),
    ...(MIGRATION_ARCHIVED ? [] : [{ label: t('Migration'), href: '/migration', supportChainIds: SUPPORT_ONLY_BSC }]),
    // Legacy products: footer only (Bez, 2026-10-03); /legacy keeps working
    // installable app (Bez, 2026-10-07): "Install app" opens its sheet, last so More's link (its first entry) is unchanged;
    // dropped inside the installed app (useMenuItems). Alerts are in Settings and on the profile, not here.
    ...pwaMoreItems(t),
  ]
  return [
    {
      id: 'markets' as const,
      label: t('Markets'),
      href: '/info',
      icon: ChartIcon,
      hideSubNav: true,
      items: [
        // the Info hub, renamed Markets; its pool and token pages stay under /info
        { label: t('Markets'), href: '/info' },
        // token pages (/token/<address>) and the token list; matchPrefix keeps the item active on a token page
        { label: t('Tokens'), href: '/tokens', matchPrefix: '/token' },
      ].map((item) => addMenuItemSupported(item, chainId)),
    },
    {
      id: 'pools' as const,
      label: t('Pools'),
      href: '/liquidity',
      icon: EarnIcon,
      fillIcon: EarnFillIcon,
      items: [
        { label: t('Liquidity'), href: '/liquidity' },
        { label: t('Farms'), href: '/farms', supportChainIds: SUPPORT_FARMS },
        { label: t('Staking Pools'), href: '/pools', supportChainIds: POOL_SUPPORTED_CHAINS },
      ].map((item) => addMenuItemSupported(item, chainId)),
    },
    {
      // Swap and MultiSwap are one page (/multiswap redirects to /swap); "MultiSwap" stays only as the page title
      id: 'swap' as const,
      label: t('Swap'),
      href: '/swap',
      icon: SyncAltIcon,
      featured: true,
      hideSubNav: true,
      showItemsOnMobile: false,
      items: [],
    },
    {
      // a first-class tab since 2026-10-06 (was the first entry of ⋯): the one competitions list, every /trading-competition* page
      // keeps it active (rewrites to -v4 and the legacy -v3 paths start with the same prefix)
      id: 'competitions' as const,
      label: t('Competitions'),
      mobileLabel: t('Compete'),
      href: '/trading-competition',
      icon: CupIcon,
      fillIcon: CupFillIcon,
      hideSubNav: true,
      showItemsOnMobile: false,
      items: [],
      // the live logos' slot is reserved (nothing live: empty); useMenuItems fills it from the competitions list
      live: { logos: [], more: 0 },
    },
    {
      id: 'profile' as const,
      label: t('Profile'),
      href: '/u',
      icon: AccountIcon,
      // not uikit's AccountFilledIcon: that is PancakeSwap's bunny
      fillIcon: ProfileFillIcon,
      hideSubNav: true,
      showItemsOnMobile: false,
      items: [],
    },
    {
      // three dots in the top bar, "More" under the dots in the bottom bar; its accessible name is "More". Like every other parent
      // its link is its first entry (a desktop click on the dots); hovering or tapping opens the list.
      id: 'more' as const,
      label: '',
      ariaLabel: t('More'),
      href: moreItems[0]?.href ?? '/prediction-markets',
      icon: MoreHorizontalIcon,
      hideSubNav: true,
      items: moreItems.map((item) => addMenuItemSupported(item, chainId)),
    },
  ].map((item) => addMenuItemSupported(item, chainId))
}

/** The phone bottom bar, five slots in this order (Bez, 2026-10-06). Profile is not a slot: see bottomNavItems. */
export const BOTTOM_NAV_ORDER = ['markets', 'competitions', 'swap', 'pools', 'more'] as const

/**
 * The bottom bar from the top bar's items: the five slots above, each with its phone label (Competitions → Compete). Profile leaves the
 * bar and becomes the first entry of More, so nothing is lost on a phone (it is also "View profile" in the wallet panel). The desktop
 * ⋯ does not list Profile, which has its own tab there.
 */
export const bottomNavItems = (items: ConfigMenuItemsType[]): ConfigMenuItemsType[] => {
  const byId = (id: ConfigMenuItemsType['id']) => items.find((item) => item.id === id)
  const profile = byId('profile')
  return BOTTOM_NAV_ORDER.map(byId)
    .filter((item): item is ConfigMenuItemsType => Boolean(item))
    .map((item) => {
      if (item.id === 'more' && profile) return { ...item, items: [{ label: profile.label, href: profile.href }, ...(item.items ?? [])] }
      return item.mobileLabel ? { ...item, label: item.mobileLabel } : item
    })
}

export default config
